import React from 'react';

import { useGetSelf, useGetUser } from '@vlabs/api/artemy/client';
import { useHasPermission } from '@vlabs/permissions';
import { useModal } from '@vlabs/uikit';
import { Button } from '@vlabs/uikit/components/controls/button';
import type { ButtonProps } from '@vlabs/uikit/components/controls/button';

import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { useResetUserPasswordTranslation } from '../i18n';
import type { ResetUserPassword } from '../types';
import { ResetUserPasswordModal } from './modal/ResetUserPasswordModal';

type ResetUserPasswordButtonProps = ResetUserPassword & Omit<ButtonProps, 'onClick'>;

const displayName = 'ResetUserPasswordButton';
const testid = buildTestid(displayName);

function ResetUserPasswordButton({
  userId = undefined,
  'data-testid': dataTestId = undefined,
  ...props
}: ResetUserPasswordButtonProps): JSX.Element | null {
  const { t } = useResetUserPasswordTranslation();
  const modal = useModal({ size: 'small' });

  const { data: self } = useGetSelf();
  const { data: user } = useGetUser(userId ?? '', {
    query: { enabled: !!userId },
  });

  const isOwnPassword = self?.user_id === userId;
  const canUpdateUsers = useHasPermission('users', 'update');
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const canResetPassword = self?.is_system || isOwnPassword || (canUpdateUsers && !user?.is_system);

  if (!userId || !self || !user || !canResetPassword) return null;

  return (
    <React.Fragment>
      {modal.wrap(<ResetUserPasswordModal
        onClose={modal.close}
        userId={userId}
      />)}

      <Button
        disabled={!userId}
        onClick={modal.open}
        variant="flat"
        {...testid(dataTestId)}
        {...props}
      >
        {t('button')}
      </Button>
    </React.Fragment>
  );
}

ResetUserPasswordButton.displayName = displayName;

export { ResetUserPasswordButton };
